import { useCallback, useState } from 'react'
import { apiFetch } from '@/api'

interface AuthStats {
  total: number
  verified: number
  unverified: number
  passwordUsers: number
  unverifiedPassword: number
  /** Unverified password users created after the given cutoff — who a flip gates. */
  unverifiedInScope: number
  cutoffISO: string
}

/**
 * Fetches email-verification rollout stats from the (root-only) admin endpoint.
 * Walks every Firebase Auth account server-side, so it's on-demand (call
 * `refresh`) rather than auto-polling. `cutoffISO` lets the caller preview the
 * in-scope count for a cutoff being edited, before it's saved.
 */
const useAuthStats = () => {
  const [stats, setStats] = useState<AuthStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async (cutoffISO: string) => {
    setLoading(true)
    setError(null)
    try {
      const qs = cutoffISO ? `?cutoffISO=${encodeURIComponent(cutoffISO)}` : ''
      const res = await apiFetch(`/admin/auth/stats${qs}`)
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}: ${body}`)
      }
      setStats((await res.json()) as AuthStats)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  return { stats, loading, error, refresh }
}

export { useAuthStats }
export type { AuthStats }
